import { computed, reactive } from 'vue'

export function useAmazonStore({ analysisSettings, lastCut, requestRaw, compressImage }) {
  const amazon = reactive({
    loading: false,
    category: '', // 产品类目
    extraContext: '',
    result: null,
    useCutRef: true,
    localPreview: '',
    keywordVolumeData: [],
    loadingVolume: false,
    showTranslation: false
  })

  const hotKeywordsCount = computed(() =>
    amazon.keywordVolumeData.filter((item) => item.hotness > 70).length
  )

  const sortedKeywordData = computed(() => [...amazon.keywordVolumeData].sort((a, b) => b.volume - a.volume))

  const setAmazonFile = async (file) => {
    if (!file) {
      return
    }
    amazon.useCutRef = false
    const compressed = await compressImage(file)
    amazon.localPreview = compressed
  }

  const getAmazonRef = () => {
    if (amazon.useCutRef && lastCut.value) {
      return lastCut.value
    }
    return amazon.localPreview
  }

  const formatBullets = (features) => {
    if (!Array.isArray(features)) {
      return ''
    }
    return features.join('\n')
  }

  // ==========================================
  // 核心生成函数：唯一保留这里的 generateAmazonCopy
  // ==========================================
  const generateAmazonCopy = async () => {
    const refImage = getAmazonRef()
    if (!refImage) {
      const message = amazon.useCutRef
        ? '暂无抠图结果，请先在抠图工作台生成，或切换为“本地上传”'
        : '请先点击“+”上传一张产品图'
      throw new Error(message)
    }

    try {
      amazon.loading = true
      amazon.result = null

      let useModel = analysisSettings.model
      if (!useModel || useModel.includes('32k')) {
        useModel = 'doubao-seed-2-0-pro-260215' // 建议文案类使用大模型
      }

      const categoryText = amazon.category ? `当前产品类目为：【${amazon.category}】。` : ''
      const userContext = amazon.extraContext ? `产品的核心卖点与补充信息：${amazon.extraContext}` : ''
      
      // 融合了你的 Word 文档规则与流量词提取规则
      const expertPrompt = `你是一个资深的亚马逊美国站SEO文案撰写师，精通Cosmo推荐算法。请根据产品图片及以下补充信息，撰写高转化率的英文Listing文案。
      补充信息：${categoryText} ${userContext}
      【文案撰写严格规则】（务必完全遵守）：
      
      1. 标题 (Title)：
      - 长度控制在110-115个字符左右。
      - 必须符合公式：Women/Womens + 风格词 + 核心大词 + Fit + Material(可选) + Comfort Feature + Occasion。
      - 注意：只有 100% Cotton, Linen, Silk, Performance Nylon 等才写入标题。如果是普通 Polyester 且无特殊功能，绝对不要写进标题，只能写在五点描述中。风格词优先级高于材质词。
      
      2. 五点描述 (Features)：
      - 每点约150个字符（含空格），风格亲切真诚友好。
      - 每点必须由1-3个简洁的单词引领，并以冒号“:”连接后面的内容。
      - ⚠️极其重要：第五点的最后一句话，必须用英文提醒消费者：“如果不确定尺码或者胸大，请拍大一码 (If you are between sizes or busty, please size up)”。
      
      3. 大描述 (Introduction)：
      - 总计1200个字符左右，需要手机端阅读友好，拒绝长难句。
      - 第一句必须是设问句或一句式概括。绝对不要使用任何加粗符号(**)或引号("")包裹！
      - 中间分3-5个段落，每个段落由一个小标题引领。小标题绝对不要使用任何加粗符号(**)或引号("")包裹！
      - 最后一句话刺激消费者购买。绝对不要使用任何加粗符号(**)或引号("")包裹！
      
      4. 流量关键词 (Keywords)：
      - 为了配合流量雷达，请提取20~25个用于搜索的高流量核心大词和长尾精准词（如: 90s aesthetic baby tee, ribbed crop top）。
      - 必须用英文逗号(,)分隔各个词组。
      - ⚠️违禁词红线：输出的所有内容中，绝对不能出现“新疆棉”、“除菌”、“杀虫”、“抗菌”等词汇，绝对不能包含 "Sexy" 和 "Sassy" 等敏感词与成人内容，不能包含任何现实中的品牌词。`

      const response = await requestRaw('/api/copywriting', {
        method: 'POST',
        body: {
          image: refImage,
          model: useModel,
          prompt: expertPrompt 
        }
      })
      
      if (response.status === 402) {
        throw new Error('算力不足或未登录，请充值后重试！')
      }
      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || '请求失败')
      }

      if (data.raw && !data.title) {
        amazon.result = {
          title: '解析异常，原始返回如下：',
          introduction: data.raw,
          features: [],
          keywords: ''
        }
      } else {
        // 不再切碎字母，而是保留逗号分隔的长尾词组供雷达使用
        if (data.keywords) {
          const rawPhrases = data.keywords.split(/[,，\n]+/)
          const uniquePhrases = [...new Set(rawPhrases.map((item) => item.trim()).filter(Boolean))]
          data.keywords = uniquePhrases.join(',')
        }
        amazon.result = data
      }
    } finally {
      amazon.loading = false
    }
  }

  const calculateHotness = (volume) => {
    if (!volume || volume <= 31) {
      return Math.max(1, Math.floor((volume || 0) / 10))
    }
    const score = (Math.log10(volume) - 1.5) * (100 / 3.5)
    return Math.max(1, Math.min(100, Math.round(score)))
  }

  const getHeatColor = (value) => {
    if (value < 30) {
      return '#60a5fa'
    }
    if (value < 60) {
      return '#34d399'
    }
    if (value < 85) {
      return '#fbbf24'
    }
    return '#f87171'
  }

  const getHeatStatus = (value) => {
    if (value < 30) {
      return 'COLD MARKET'
    }
    if (value < 60) {
      return 'STEADY FLOW'
    }
    if (value < 85) {
      return 'HIGH GREED'
    }
    return 'EXTREME FRENZY'
  }

  // ==========================================
  // 雷达流量分析函数
  // ==========================================
  const analyzeTrafficAll = async () => {
    if (!amazon.result?.keywords) {
      return
    }

    // 雷达直接拿着完整的长尾词组去查询
    const rawPhrases = amazon.result.keywords.split(/[,，]+/)
    const keywords = [...new Set(rawPhrases.map((item) => item.trim().toLowerCase()).filter(Boolean))]
    console.log("准备送给雷达分析的长尾词组：", keywords)
    
    amazon.loadingVolume = true
    amazon.keywordVolumeData = []

    for (const keyword of keywords) {
      try {
        const response = await requestRaw('/api/amazon/traffic-radar', {
          method: 'POST',
          body: { keyword }
        })
        if (response.status === 402) {
          throw new Error('算力不足或未登录，请充值后重试！')
        }
        const data = await response.json()
        const volume = data.volume !== undefined ? data.volume : 0
        const hotness = calculateHotness(volume)

        amazon.keywordVolumeData.push({
          keyword, // 这里传给后端的是完整词组，如 "y2k crop top"
          volume,
          hotness,
          updated: data.updated || 'N/A'
        })
      } catch (error) {
        // 单个词失败不影响其余词
      }
    }

    amazon.loadingVolume = false
  }

  return {
    amazon,
    hotKeywordsCount,
    sortedKeywordData,
    setAmazonFile,
    getAmazonRef,
    formatBullets,
    generateAmazonCopy,
    getHeatColor,
    getHeatStatus,
    analyzeTrafficAll
  }
}